import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { PlanType } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { translate } from '~l10n/client';
import { clearAiQuota } from '~shared/stores/aiQuotaStore';
import { InviteTeamMembersDialog } from '~teacher/components/Account/TeamMembers/InviteTeamMembersDialog';
import { OverPurchasedSeatsDialog } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog';
import {
  Error as ErrorIcon,
  Pending as PendingIcon,
  Success as SuccessIcon,
} from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import { accountKeys, fetchAccount } from '~teacher/hook/account';
import { useTeams } from '~teacher/hook/team';
import { loadPermissions } from '~teacher/util/proCheck';

import { assertUnreachable } from '../../../util/assertUnreachable';
import { sleep } from '../../../util/sleep';
import { selectedTeamIdAtom } from '../Account/atoms';

type SubscriptionStatus = 'idle' | 'loading' | 'error' | 'success';

export const PostPurchase = () => {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>('idle');

  const queryClient = useQueryClient();
  useEffect(() => {
    async function checkStripeStatus() {
      const MAX_RETRIES = 3;
      setSubscriptionStatus('loading');

      let retryCount = 0;
      let isPro = false;

      do {
        await queryClient.invalidateQueries(accountKeys.account);
        await sleep(2000);

        await loadPermissions(() => fetchAccount(queryClient)).then(
          (account) => {
            isPro = account.isPro;
          }
        );
        retryCount++;
      } while (retryCount < MAX_RETRIES && !isPro);

      if (!isPro) {
        Sentry.captureException(
          new Error('Could not retrieve stripe subscription')
        );
        setSubscriptionStatus('error');
      } else {
        clearAiQuota();
        setSubscriptionStatus('success');
      }
    }

    if (subscriptionStatus === 'idle') {
      checkStripeStatus().then(() => /* noop */ {});
    }
  }, [queryClient, subscriptionStatus]);

  switch (subscriptionStatus) {
    case 'idle':
    case 'loading': {
      return <SubscriptionStatusLoading />;
    }
    case 'error': {
      return <SubscriptionStatusError />;
    }
    case 'success': {
      return <SubscriptionStatusSuccess />;
    }
    default: {
      assertUnreachable(subscriptionStatus);
    }
  }
};

function SubscriptionStatusLoading() {
  return (
    <div
      data-testid="post-purchase-loading"
      className="flex flex-col items-center justify-center w-full p-6"
    >
      <PendingIcon />
      <span className="py-5 font-display text-md font-500 text-blue-900">
        {translate('Please wait…')}
      </span>
    </div>
  );
}

function SubscriptionStatusError() {
  return (
    <div
      data-testid="post-purchase-error"
      className="flex flex-col items-center justify-center w-full p-6"
    >
      <ErrorIcon />
      <span className={'py-5 font-display text-md font-500 text-blue-900'}>
        {translate('Something Is Taking Longer Than Usual')}
      </span>
      <span className="max-w-2xl font-sans text-base leading-8 text-blue-900 text-center">
        {translate(
          'Your recent purchase is being processed. To avoid a duplicate charge, kindly wait until you receive an email receipt before making another purchase. If the problem persists, please contact Support.'
        )}
      </span>
    </div>
  );
}

function SubscriptionStatusSuccess() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTeamId] = useAtom(selectedTeamIdAtom);

  const teamSubscriptionPlan = useTeams({
    select: (teams) => {
      const team = teams.find((team) => team.id === selectedTeamId);
      return team?.subscription?.plan;
    },
  });

  const hasInvited = useRef(false);
  useEffect(() => {
    hasInvited.current = false;
  }, []);

  useEffect(() => {
    if (
      teamSubscriptionPlan.data === PlanType.Pro &&
      hasInvited.current === false
    ) {
      console.log('Inviting team members');
      setIsOpen(true);
      hasInvited.current = true;
    }
  }, [teamSubscriptionPlan.data]);

  return (
    <>
      <div
        data-testid="post-purchase-success"
        className="flex flex-col items-center justify-center w-full p-6"
      >
        <SuccessIcon />
        <span className="py-5 font-display text-md font-500 text-blue-900">
          {translate('Payment Confirmed')}
        </span>
        <span className="max-w-2xl font-sans text-base leading-8 text-blue-900 text-center">
          {translate(
            'We sent an email of your receipt. Your plan will be active shortly. If you have any questions, contact Support.'
          )}
        </span>
      </div>
      <InviteTeamMembersDialog
        isSkippable
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        teamId={selectedTeamId}
      />
      <OverPurchasedSeatsDialog />
    </>
  );
}
